import { IEmployee } from "./Employee";
import { MealType } from "./MealType";
import { IDisplayQuantity, MeasurementUnit } from "./MeasurementUnit";

export type EventTimePerMeal = Partial<Record<MealType, string>>;

export const EVENT_TYPES = ["Wedding", "Sangeet", "Simant Pujan"] as const;

export enum CateringEventStatus {
  DRAFT = "DRAFT",
  CREATED = "CREATED",
  PO_CONSOLIDATED = "PO_CONSOLIDATED",
  DONE = "DONE",
}

export const CateringEventStatusList = Object.values(CateringEventStatus);

// ── Core view types (mirror backend CateringEventView) ──────────────────────

export interface ICateringEventView {
  id: string;
  name: string;
  branchId: string;
  eventDate: number;
  mealType: MealType;
  numberOfPeople: number;
  status: CateringEventStatus;
  createdByEmployee: IEmployee;
  creationTime: number;
  remark?: string;
  consolidationPointer?: string;
  customerName: string;
  customerContactNumber?: string;
  customerEmail?: string;
  eventTimePerMeal?: EventTimePerMeal;
  eventType?: string;
}

export interface IMaterialQuantityView {
  materialId: string;
  materialName: string;
  unit: MeasurementUnit;
  quantity: number;
  displayQuantity: IDisplayQuantity;
}

export interface IMenuItemBreakdownView {
  sellableItemId: string;
  sellableItemName: string;
  rawMaterials: IMaterialQuantityView[];
  semiPreparedMaterials: IMaterialQuantityView[];
  rawMaterialOverrides: Record<string, IDisplayQuantity>;
  semiPreparedMaterialOverrides: Record<string, IDisplayQuantity>;
}

// Extends CateringEventView — returned by /menu and /menuDetails
export interface ICateringEventDetailedView extends ICateringEventView {
  menuItemBreakdowns: IMenuItemBreakdownView[];
  totalRawMaterials: IMaterialQuantityView[];
  totalSemiPreparedMaterials: IMaterialQuantityView[];
}

// ── Request types ───────────────────────────────────────────────────────────

export interface ICreateCateringEventRequest {
  name: string;
  branchId: string;
  eventDate: number; // epoch ms
  mealType: MealType;
  numberOfPeople: number;
  customerName?: string;
  customerContactNumber?: string;
  customerEmail?: string;
  remark?: string;
  eventTimePerMeal?: EventTimePerMeal;
  eventType?: string;
}

// Backend UpdateCateringEventRequest has the same fields as Create
export type IUpdateCateringEventRequest = ICreateCateringEventRequest;

export interface IConfigureCateringEventMenuRequest {
  sellableItemIds: string[];
}

export interface IOverrideMaterialsRequest {
  perItemRawMaterialQuantityOverrides?: Record<string, Record<string, number>>;
  perItemSemiPreparedMaterialQuantityOverrides?: Record<string, Record<string, number>>;
}

// ── Consolidation types (PO flow) ───────────────────────────────────────────

export interface ICateringConsolidationStep1Request {
  cateringEventIds: string[];
}

export interface ISupplierOption {
  supplierId: string;
  supplierName: string;
}

export interface IMaterialSupplierOption {
  materialId: string;
  materialName: string;
  unit: MeasurementUnit;
  totalQuantity: number;
  displayQuantity: IDisplayQuantity;
  availableSuppliers: ISupplierOption[];
}

export interface ICateringConsolidationStep1Response {
  materialSupplierOptions: IMaterialSupplierOption[];
}

export interface ICateringConsolidationStep2Request {
  cateringEventIds: string[];
  materialIdToSupplierId: Record<string, string>;
}

export interface IMaterialQuantityEntry {
  materialId: string;
  materialName: string;
  unit: MeasurementUnit;
  quantity: number;
  displayQuantity: IDisplayQuantity;
}

export interface IProposedPurchaseOrder {
  supplierId: string;
  supplierName: string;
  branchId: string;
  branchName: string;
  materials: IMaterialQuantityEntry[];
}

export interface ICateringConsolidationConfirmationResponse {
  proposedPurchaseOrders: IProposedPurchaseOrder[];
}

export interface ICreatedPurchaseOrder {
  id: string;
  vanityNumber: string;
}

export interface IProcessConsolidationResponse {
  createdPurchaseOrders: ICreatedPurchaseOrder[];
}
